import { useEffect, useState, useRef } from 'react';
import { ConfigProvider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchShiftDetails,
  fetchDailyTasks,
  fetchPerformanceMetrics,
  fetchAnnouncements,
} from '../../../redux/StaffDashboard';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { ThemeProvider } from 'styled-components';
import MetricCard from '../../../commonComponents/MetricCard/MetricCard';
import ShiftDetailsSection from './ShiftDetailsSection';
import DailyTasksSection from './DailyTasksSection';
import PerformanceMetricsSection from './PerformanceMetricsSection';
import AnnouncementsSection from './AnnouncementsSection';
import { Container, HeaderSection, MetricsGrid, DashboardTitle } from './styles';
import {
  METRIC_TYPES,
  SECTION_TITLES,
  DEFAULT_SHIFT_DETAILS,
  DEFAULT_DAILY_TASKS,
  DEFAULT_PERFORMANCE_METRICS,
  DEFAULT_ANNOUNCEMENTS,
} from './constants';

const DashboardStaff: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [selectedItem, setSelectedItem] = useState<string | null>(METRIC_TYPES.SHIFT_DETAILS);

  const dispatch = useDispatch<AppDispatch>();
  const hasFetchedRef = useRef(false);

  const {
    shiftDetails,
    dailyTasks,
    performanceMetrics,
    announcements,
    // loading,
  } = useSelector((state: RootState) => state.staffDashboard);

  // const isLoading =
  //   loading.shiftDetails ||
  //   loading.dailyTasks ||
  //   loading.performanceMetrics ||
  //   loading.announcements;

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchShiftDetails());
      dispatch(fetchDailyTasks());
      dispatch(fetchPerformanceMetrics());
      dispatch(fetchAnnouncements());
    }
  }, [dispatch]);

  const handleClick = (item: string) => {
    setSelectedItem(item === selectedItem ? null : item);
  };

  const displayShiftDetails = shiftDetails || DEFAULT_SHIFT_DETAILS;
  const displayDailyTasks = dailyTasks.length > 0 ? dailyTasks : DEFAULT_DAILY_TASKS;
  const displayPerformanceMetrics =
    performanceMetrics || DEFAULT_PERFORMANCE_METRICS;
  const displayAnnouncements =
    announcements.length > 0 ? announcements : DEFAULT_ANNOUNCEMENTS;

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <DashboardTitle level={2}>Dashboard</DashboardTitle>
          </HeaderSection>

          <MetricsGrid>
            <MetricCard
              title={SECTION_TITLES.SHIFT_DETAILS}
              onClick={() => handleClick(METRIC_TYPES.SHIFT_DETAILS)}
              isSelected={selectedItem === METRIC_TYPES.SHIFT_DETAILS}
              variant="simple"
            />
            <MetricCard
              title={SECTION_TITLES.DAILY_TASKS}
              onClick={() => handleClick(METRIC_TYPES.DAILY_TASKS)}
              isSelected={selectedItem === METRIC_TYPES.DAILY_TASKS}
              variant="simple"
            />
            <MetricCard
              title={SECTION_TITLES.PERFORMANCE_METRICS}
              onClick={() => handleClick(METRIC_TYPES.PERFORMANCE_METRICS)}
              isSelected={selectedItem === METRIC_TYPES.PERFORMANCE_METRICS}
              variant="simple"
            />
            <MetricCard
              title={SECTION_TITLES.ANNOUNCEMENTS}
              onClick={() => handleClick(METRIC_TYPES.ANNOUNCEMENTS)}
              isSelected={selectedItem === METRIC_TYPES.ANNOUNCEMENTS}
              variant="simple"
            />
          </MetricsGrid>

          <ShiftDetailsSection
            shiftDetails={displayShiftDetails}
            isOpen={selectedItem === METRIC_TYPES.SHIFT_DETAILS}
          />
          <DailyTasksSection
            dailyTasks={displayDailyTasks}
            isOpen={selectedItem === METRIC_TYPES.DAILY_TASKS}
            onClose={() => handleClick(METRIC_TYPES.DAILY_TASKS)}
          />
          <PerformanceMetricsSection
            performanceMetrics={displayPerformanceMetrics}
            isOpen={selectedItem === METRIC_TYPES.PERFORMANCE_METRICS}
            onClose={() => handleClick(METRIC_TYPES.PERFORMANCE_METRICS)}
          />
          <AnnouncementsSection
            announcements={displayAnnouncements}
            isOpen={selectedItem === METRIC_TYPES.ANNOUNCEMENTS}
            onClose={() => handleClick(METRIC_TYPES.ANNOUNCEMENTS)}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default DashboardStaff;
